function escHtml(s) { const d = document.createElement("div"); d.textContent = s; return d.innerHTML }

class CartDrawer {
  constructor(root) {
    this.root = root
    this.dialog = root.querySelector("dialog")
    this.overlay = root.querySelector("[data-close]")
    this.closeBtns = root.querySelectorAll("[data-close]")
    this.itemsEl = root.querySelector("[data-items]")
    this.summaryEl = root.querySelector("[data-summary]")

    // Upsell cache
    this._upsellCache = null
    this._upsellCartKeys = null
    this._upsellAbort = null

    this.dialog?.addEventListener("click", (e) => {
      const rect = this.dialog.getBoundingClientRect()
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        this.close()
      }
    })

    this.bind()
    this.loadEmptyCartPicks()
  }

  flyToCart(sourceEl) {
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
      const cartIcon = this.root.querySelector("[data-cart-open]")
      if (!cartIcon || !sourceEl) return

      const src = sourceEl.getBoundingClientRect()
      const dest = cartIcon.getBoundingClientRect()

      const dot = document.createElement("div")
      dot.style.cssText = `
        position:fixed; left:0; top:0; width:16px; height:16px; z-index:9999;
        pointer-events:none; will-change:transform,opacity;
        border-radius:50%; background:#5C0A13;
      `
      document.body.appendChild(dot)

      const startX = src.left + src.width / 2 - 8
      const startY = src.top + src.height / 2 - 8
      const endX = dest.left + dest.width / 2 - 8
      const endY = dest.top + dest.height / 2 - 8

      dot.animate([
        { transform: `translate(${startX}px, ${startY}px) scale(1)`, opacity: 1 },
        { transform: `translate(${(startX + endX) / 2}px, ${Math.min(startY, endY) - 40}px) scale(.8)`, opacity: 0.9 },
        { transform: `translate(${endX}px, ${endY}px) scale(.4)`, opacity: 0 },
      ], {
        duration: 600,
        easing: "cubic-bezier(.22,1,.36,1)",
        fill: "forwards",
      }).onfinish = () => dot.remove()
    } catch (_) { /* noop */ }
  }

  async loadEmptyCartPicks() {
    const container = this.root.querySelector("[data-empty-cart-picks]")
    if (!container) return

    const ccy = window.Shopify?.currency?.active || "AUD"
    const fmt = (v) => {
      const n = parseFloat(v) / 100
      try { return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n) } catch (_) { return `$${n.toFixed(2)}` }
    }
    const fmtRaw = (v) => {
      const n = parseFloat(v)
      try { return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n) } catch (_) { return `$${n.toFixed(2)}` }
    }

    try {
      const recent = JSON.parse(sessionStorage.getItem("lbj_recently_viewed") || "[]")
      if (recent.length > 0) {
        const heading = container.closest(".cart-empty__picks")?.querySelector(".cart-empty__picks-title")
        if (heading) heading.textContent = "Recently Viewed"
        container.innerHTML = recent.slice(0, 4).map(p => `
          <a href="${escHtml(p.url)}" class="empty-pick">
            ${p.image ? `<img src="${escHtml(p.image)}" alt="${escHtml(p.title)}" width="80" height="80" loading="lazy" class="empty-pick__img">` : ""}
            <span class="empty-pick__title">${escHtml(p.title)}</span>
            <span class="empty-pick__price">${fmt(p.price)}</span>
          </a>
        `).join("")
        return
      }
    } catch (_) {}

    try {
      const res = await fetch("/collections/all/products.json?limit=4&sort_by=best-selling")
      if (!res.ok) return
      const data = await res.json()
      const products = data?.products || []
      if (!products.length) return

      container.innerHTML = products.slice(0, 4).map(p => {
        const img = p.images?.[0]?.src || ""
        const price = p.variants?.[0]?.price || "0"
        return `
          <a href="/products/${p.handle}" class="empty-pick">
            ${img ? `<img src="${img}" alt="${escHtml(p.title)}" width="80" height="80" loading="lazy" class="empty-pick__img">` : ""}
            <span class="empty-pick__title">${escHtml(p.title)}</span>
            <span class="empty-pick__price">${fmtRaw(price)}</span>
          </a>
        `
      }).join("")
    } catch (_) {}
  }

  bind() {
    // Open drawer
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-cart-open]")) {
        e.preventDefault()
        this.open()
        return
      }

      // Close
      if (e.target.hasAttribute("data-close") || e.target === this.dialog) {
        this.close()
      }
    })

    // Gift wrap line item + cart attribute toggle
    document.addEventListener("change", async (e) => {
      const checkbox = e.target.closest(".cart-gift-wrap-check")
      if (!checkbox) return
      const variantId = checkbox.dataset.giftWrapVariant
      if (!variantId) return

      checkbox.disabled = true
      const label = checkbox.closest(".cart-gift-wrap-label")
      if (label) label.style.opacity = "0.5"

      const jsonHeaders = { "Content-Type": "application/json" }
      try {
        if (checkbox.checked) {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: jsonHeaders,
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          })
          await fetch("/cart/update.js", {
            method: "POST",
            headers: jsonHeaders,
            body: JSON.stringify({ attributes: { "Gift Wrap": "Yes" } }),
          })
        } else {
          await Promise.all([
            fetch("/cart/change.js", {
              method: "POST",
              headers: jsonHeaders,
              body: JSON.stringify({ id: variantId, quantity: 0 }),
            }),
            fetch("/cart/update.js", {
              method: "POST",
              headers: jsonHeaders,
              body: JSON.stringify({ attributes: { "Gift Wrap": "" } }),
            }),
          ])
        }
        await this.refresh()
      } catch (_) {
        checkbox.checked = !checkbox.checked
      } finally {
        checkbox.disabled = false
        if (label) label.style.opacity = ""
      }
    })

    // Listen for cart:update events (from PDP add-to-cart, quick-add, etc.)
    document.addEventListener("cart:update", async (e) => {
      // If the event came from INSIDE the drawer (theme's CartItemsComponent),
      // the theme already handled the DOM update via morphSection — don't re-render.
      // Just refresh upsells in the background.
      if (this.root.contains(e.target)) {
        this._upsellCache = null
        this.loadUpsells()
        return
      }

      // External event (PDP add-to-cart, quick-add, etc.) — do a full refresh + open
      if (e.detail?.source) this.flyToCart(e.detail.source)

      await this.refresh()

      if (!e.detail?.data?.didError) {
        this.open()
      }
    })
  }

  async refresh() {
    const sectionId = this.root.dataset.sectionId
    if (!sectionId) return

    const base = window.location.pathname || "/"
    const url = `${base}?section_id=${encodeURIComponent(sectionId)}&_=${Date.now()}`

    const res = await fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
      cache: "no-store",
    })

    const html = await res.text()
    const dom = new DOMParser().parseFromString(html, "text/html")

    const nextDrawer = dom.querySelector("#cart-drawer")
    if (!nextDrawer) return

    const nextItemsComponent = nextDrawer.querySelector("cart-items-component")
    const currentItemsComponent = this.root.querySelector("cart-items-component")

    if (nextItemsComponent && currentItemsComponent) {
      currentItemsComponent.innerHTML = nextItemsComponent.innerHTML
    }

    const nextDialog = nextDrawer.querySelector("dialog.cart-drawer__dialog")
    const currentDialog = this.root.querySelector("dialog.cart-drawer__dialog")

    if (nextDialog && currentDialog) {
      currentDialog.className = nextDialog.className
    }

    this._upsellCache = null
    this.loadUpsells()
  }

  _syncHeaderHeight() {
    // Mirror the main site exactly: cart header = announcement bar height,
    // gold line lives in the white area below (matching .header__row--top::before
    // which paints gold-over-white at top:0). This way the gold is painted
    // over the same white background on both sides — opacity bleeds identically.
    const headerRow = document.querySelector('.header__row--top')
    let goldTop = 0
    if (headerRow) {
      goldTop = Math.round(headerRow.getBoundingClientRect().top)
    }
    if (goldTop <= 0) {
      const annc = document.querySelector('.lb-announcement-bar')
        || document.querySelector('.announcement-bar')
      if (annc) goldTop = Math.round(annc.getBoundingClientRect().height)
    }
    if (goldTop <= 0) return
    // Set globally so all drawers (cart, wishlist, etc.) use the correct height via CSS var
    document.documentElement.style.setProperty('--annc-bar-height', goldTop + 'px')
    this.root.querySelectorAll('.lbj-cart-header').forEach(el => {
      el.style.height = goldTop + 'px'
    })
  }

  open() {
    this._intentOpen = true
    this.root.hidden = false
    this.root.setAttribute("aria-hidden", "false")

    this._syncHeaderHeight()

    document.querySelectorAll('.menu-list__link[aria-expanded="true"]').forEach(function (l) {
      l.setAttribute('aria-expanded', 'false')
      l.classList.remove('menu-list__link--active')
    })

    document.documentElement.style.scrollBehavior = 'auto'
    const savedY = window.scrollY
    document.body.style.width = "100%"
    document.body.style.position = "fixed"
    document.body.style.top = `-${savedY}px`
    document.body.dataset.cartScrollY = savedY
    document.body.dataset.drawerOpen = '1'

    if (this.dialog && !this.dialog.open) {
      this.dialog.show()
    }

    requestAnimationFrame(() => {
      this.root.classList.add("is-open")
    })

    this.loadUpsells()
  }

  close() {
    this._intentOpen = false
    this.root.classList.remove("is-open")
    this.root.setAttribute("aria-hidden", "true")

    if (this.dialog && this.dialog.open) {
      this.dialog.close()
    }

    const scrollY = parseInt(document.body.dataset.cartScrollY || "0", 10)
    document.body.style.position = ""
    document.body.style.top = ""
    document.body.style.width = ""
    delete document.body.dataset.cartScrollY
    window.scrollTo(0, scrollY)
    document.documentElement.style.scrollBehavior = ''
    delete document.body.dataset.drawerOpen

    setTimeout(() => {
      this.root.hidden = true
    }, 250)
  }

  async loadUpsells() {
    const wrap = this.root.querySelector("[data-cart-upsell]")
    const container = this.root.querySelector("[data-upsell-items]")
    if (!wrap || !container) return

    if (this._upsellAbort) this._upsellAbort.abort()
    this._upsellAbort = new AbortController()
    const signal = this._upsellAbort.signal

    const PRIORITY_HANDLES = [
      "390579-gold-jewellery-cleaner-liquid-1045",
      "jewellery-cleaning-cloth"
    ]

    try {
      const cart = await fetch("/cart.js", { credentials: "same-origin", cache: "no-store", signal }).then(r => r.json())
      if (!cart.items || cart.items.length === 0) {
        wrap.hidden = true
        return
      }

      const cartKeys = cart.items.map(i => i.handle).sort().join(",")

      if (this._upsellCache && this._upsellCartKeys === cartKeys) {
        await this._renderUpsells(this._upsellCache, cart, wrap, container)
        return
      }

      const cartHandles = new Set(cart.items.map(i => i.handle))
      const cartIds = new Set(cart.items.map(i => i.product_id))
      let products = []
      const seen = new Set()

      const recPromises = []
      for (const item of cart.items) {
        recPromises.push(
          fetch(`/recommendations/products.json?product_id=${item.product_id}&limit=8&intent=complementary`, { signal })
            .then(r => r.ok ? r.json() : { products: [] })
            .catch(e => { if (e.name === 'AbortError') throw e; return { products: [] } })
        )
        recPromises.push(
          fetch(`/recommendations/products.json?product_id=${item.product_id}&limit=4&intent=related`, { signal })
            .then(r => r.ok ? r.json() : { products: [] })
            .catch(e => { if (e.name === 'AbortError') throw e; return { products: [] } })
        )
      }

      const recResults = await Promise.all(recPromises)
      for (const data of recResults) {
        for (const p of (data.products || [])) {
          if (cartIds.has(p.id) || seen.has(p.id)) continue
          if (!p.available) continue
          seen.add(p.id)
          products.push(p)
        }
      }

      products.sort((a, b) => a.price - b.price)

      for (const handle of PRIORITY_HANDLES) {
        if (cartHandles.has(handle)) continue
        if (products.some(p => p.handle === handle)) {
          products = [products.find(p => p.handle === handle), ...products.filter(p => p.handle !== handle)]
          continue
        }
        try {
          const pRes = await fetch(`/products/${handle}.js`, { signal })
          if (pRes.ok) {
            const pData = await pRes.json()
            if (pData.available) products = [pData, ...products]
          }
        } catch (_) {}
      }

      if (products.length === 0) {
        wrap.hidden = true
        return
      }

      this._upsellCache = products
      this._upsellCartKeys = cartKeys

      await this._renderUpsells(products, cart, wrap, container)
    } catch (_) {
      wrap.hidden = true
    }
  }

  _fmtPrice(cents, ccy) {
    try {
      const raw = new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(cents / 100)
      return raw.replace(/^[A-Z]{0,3}\s*/, "").replace(/\.00$/, "")
    } catch (_) {
      const val = cents / 100
      return val % 1 === 0 ? `$${val.toFixed(0)}` : `$${val.toFixed(2)}`
    }
  }

  async _renderUpsells(products, cart, wrap, container) {
    const ccy = window.Shopify?.currency?.active || "AUD"
    const available = products.filter(p => !cart.items.some(i => i.product_id === p.id))

    if (available.length === 0) {
      wrap.hidden = true
      return
    }

    const toShow = available.slice(0, 3)

    const closingSaleMap = new Map()
    try {
      const metaPromises = toShow.map(p =>
        fetch(`/products/${p.handle}.json`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      )
      const metaResults = await Promise.all(metaPromises)
      metaResults.forEach((data, i) => {
        if (!data?.product) return
        const prod = data.product
        const tags = (prod.tags || "").split(",").map(t => t.trim().toLowerCase())
        const hasTag = tags.includes("closing sale item")
        if (hasTag) closingSaleMap.set(toShow[i].id, true)
      })
    } catch (_) {}

    container.innerHTML = toShow.map(p => {
      const img = p.featured_image || ""
      const isClosingSale = closingSaleMap.has(p.id)
      const pTags = Array.isArray(p.tags) ? p.tags.map(t => t.toLowerCase()) : (p.tags || "").split(",").map(t => t.trim().toLowerCase())
      const tagMatch = pTags.includes("closing sale item")
      const showSale = isClosingSale || tagMatch

      let priceHtml = ""
      if (showSale) {
        const originalText = this._fmtPrice(p.price, ccy)
        const salePrice = Math.round(p.price * 0.65)
        const saleText = this._fmtPrice(salePrice, ccy)
        priceHtml = `<s class="upsell-card__was">${originalText}</s> <span class="upsell-card__sale">${saleText}</span>`
      } else {
        priceHtml = this._fmtPrice(p.price, ccy)
      }

      const v = p.variants && p.variants[0]
      return `
        <div class="upsell-card">
          <a href="${p.url}" class="upsell-card__link">
            ${img ? `<img src="${img}" alt="${escHtml(p.title)}" width="120" height="120" loading="lazy" class="upsell-card__img">` : ""}
          </a>
          <span class="upsell-card__price">${priceHtml}</span>
          ${v && v.available ? '<button type="button" class="upsell-card__add" data-upsell-add="' + v.id + '" aria-label="Add ' + escHtml(p.title) + ' to bag"><svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.3" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/><path stroke="currentColor" stroke-linecap="round" stroke-width="1.3" d="M14.608 12.563v5m2.5-2.5h-5"/></svg></button>' : ""}
        </div>
      `
    }).join("")

    wrap.hidden = false

    container.querySelectorAll("[data-upsell-add]").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        e.preventDefault()
        e.stopPropagation()
        const id = btn.dataset.upsellAdd
        btn.disabled = true
        btn.classList.add("is-loading")
        btn.innerHTML = '<span class="upsell-spinner"></span>'
        try {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, quantity: 1 }),
          })
          btn.classList.remove("is-loading")
          btn.classList.add("is-added")
          btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>'
          this._upsellCache = null
          await this.refresh()
        } catch (_) {
          btn.classList.remove("is-loading")
          btn.textContent = "Error"
        }
      }, { once: true })
    })
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCartDrawer)
} else {
  initCartDrawer()
}

function initCartDrawer() {
  const root = document.getElementById("cart-drawer")
  if (root) {
    window.cartDrawer = new CartDrawer(root)
  }
  window.addEventListener("load", () => {
    if (window.cartDrawer?._intentOpen && !window.cartDrawer.dialog?.open) {
      window.cartDrawer.open()
    }
  }, { once: true })
}
